import { CSVHelper } from "@/utils/csv_helper"
import { ConnStateInfo } from "."

/// 写入统计csv文件的表头
const headers = [
  "qpSum",
  "nackCount",
  "firCount",
  "pliCount",
  "packetsLost",
  "freezeCount",
  "totalFreezesDuration",
  "keyFramesDecoded",
  "pauseCount",
  "totalSquaredInterFrameDelay",
  "framesDropped",
  "totalAssemblyTime",
  "retransmittedBytesReceived",
  "framesAssembledFromMultiplePackets",
  "framesReceived",
  "jitterBufferMinimumDelay",
  "jitterBufferDelay",
  "jitterBufferTargetDelay",
  "minPlayoutDelay",
  "totalPausesDuration",
  "headerBytesReceived",
  "fecPacketsReceived",
  "jitterBufferEmittedCount",
  "bytesReceived",
  "totalProcessingDelay",
  "jitter",
  "packetsReceived",
  "fecBytesReceived",
  "totalDecodeTime",
  "framesDecoded",
  "retransmittedPacketsReceived",
  "totalInterFrameDelay",
]

export class _ConnStatsProcessor {
  // candidate-pair related
  private lastCandidatePairBytesReceived = 0
  private candidatePairCurrentRtt = 0
  private candidatePairCurBytesReceived = 0

  // transport related
  private lastTransportBytesReceived = 0
  private transportCurBytesReceived = 0

  // inbound-rtp video related
  private lastInboundVideoPacketsLost = 0
  private lastInboundVideoPacketsReceived = 0
  private lastInboundVideoBytesReceived = 0
  private lastInboundVideoFramesReceived = 0
  private lastInboundVideoFramesDecoded = 0
  private inboundVideoFps = 0
  private inboundVideoCurBytesReceived = 0
  private inboundVideoLostRate = 0.0
  private inboundVideoCurFrameReceived = 0
  private inboundVideoCurFramesDecoded = 0
  private inboundVideoFrameWidth = 0
  private inboundVideoFrameHeight = 0
  private inboundVideoFreezeCount = 0
  private inboundVideoTotalFreezesDuration = 0
  private inboundVideoDecoder = "unknown"
  private inboundVideoFramesReceived = 0
  private inboundVideoFramesDecoded = 0
  private inboundVideoPacketsReceived = 0
  private inboundVideoFramesDropped = 0
  private inboundVideoKeyFramesDecoded = 0
  private inboundVideoFecPacketsReceived = 0
  private inboundVideoFecBytesReceived = 0
  private lastLoggedVideoCodec: string | null = null
  private lastLoggedInboundVideoFecPacketsReceived: number | null = null
  private lastLoggedInboundVideoFecBytesReceived: number | null = null
  private inFreeze = false
  private freezeStartTime = 0

  // inbound-rtp audio related
  private lastInboundAudioBytesReceived = 0
  private inboundAudioIsPlayingAudio = false

  // outbound-rtp audio related
  private lastOutboundAudioBytesSent = 0
  private outboundAudioBytesPerSecond = 0
  private outboundAudioEncoder: string | null = null
  private outboundAudioMid = ""

  // codec related
  private codecMimeType: string | null = null

  private csvHelper?: CSVHelper

  // outbound / remote-inbound (keep unchanged as per requirement)
  private lastOutboundBytesSent = 0
  private outboundVideoBytesPerSecond = 0
  private outboundVideoFrameWidth = 0
  private outboundVideoFrameHeight = 0
  private outboundVideoFps = 0
  private outboundVideoEncoder: string | null = null
  private outboundVideoTotalEncodeTime = 0
  private outboundVideoFramesEncoded = 0
  private lastRemoteInboundPacketsLost = 0
  private lastRemoteInboundPacketsReceived = 0
  private remoteInboundVideoLostRate = 0
  private remoteInboundVideoJitter = 0
  private outboundVideoMid = ""
  private lastLoggedVideoEncoder: string | null = null

  private processCandidatePair(info: any) {
    const { state, nominated, writable } = info
    // Safari 不一定暴露 writable/nominated 字段；这些字段缺失时，
    // succeeded 的候选对仍然可能是当前可用链路。
    if (state === "succeeded" && nominated !== false && writable !== false) {
      const rttSeconds =
        typeof info.currentRoundTripTime === "number"
          ? info.currentRoundTripTime
          : info.roundTripTime
      if (typeof rttSeconds === "number" && Number.isFinite(rttSeconds)) {
        this.candidatePairCurrentRtt = rttSeconds * 1000
      }
      const bytesReceived =
        typeof info.bytesReceived === "number" ? info.bytesReceived : 0
      this.candidatePairCurBytesReceived = Math.max(
        bytesReceived - this.lastCandidatePairBytesReceived,
        0
      )
      this.lastCandidatePairBytesReceived = bytesReceived
    }
  }

  private processTransport(info: any) {
    const bytesReceived = info.bytesReceived || 0
    this.transportCurBytesReceived = Math.max(
      bytesReceived - this.lastTransportBytesReceived,
      0
    )
    this.lastTransportBytesReceived = bytesReceived
  }

  private resolveCodecMimeType(
    codecId: unknown,
    codecById: Map<string, any>
  ): string | null {
    if (typeof codecId !== "string") return null
    return codecById.get(codecId)?.mimeType || null
  }

  private processInboundRTPVideo(info: any, codecById: Map<string, any>) {
    // console.log("SDP 视频统计 raw inbound-rtp(video):", info)

    // 计算丢包率  Process packets for lost rate calculation
    const packetsReceived = info.packetsReceived || 0
    const packetsLost = info.packetsLost || 0
    const curPacketsLost = Math.max(
      packetsLost - this.lastInboundVideoPacketsLost,
      0
    )
    const curPacketsReceived = Math.max(
      packetsReceived - this.lastInboundVideoPacketsReceived,
      0
    )
    this.lastInboundVideoPacketsLost = packetsLost
    this.lastInboundVideoPacketsReceived = packetsReceived
    if (curPacketsLost + curPacketsReceived > 0) {
      this.inboundVideoLostRate =
        curPacketsLost / (curPacketsLost + curPacketsReceived)
    }

    // 计算码率 Process bytes received
    const bytesReceived = info.bytesReceived || 0
    this.inboundVideoCurBytesReceived = Math.max(
      bytesReceived - this.lastInboundVideoBytesReceived,
      0
    )
    this.lastInboundVideoBytesReceived = bytesReceived

    // 帧率 Process FPS
    if (info.framesPerSecond !== undefined) {
      this.inboundVideoFps = info.framesPerSecond
      const tsSec = info.timestamp / 1000
      if (info.framesPerSecond === 0) {
        if (!this.inFreeze) {
          this.inFreeze = true
          this.freezeStartTime = tsSec
          this.inboundVideoFreezeCount += 1
        }
      } else if (this.inFreeze) {
        this.inFreeze = false
        this.inboundVideoTotalFreezesDuration += tsSec - this.freezeStartTime
      }
    }

    // 计算当前 1s 内收到的帧数和解码的帧数  Process frame counts
    const framesReceived = info.framesReceived || 0
    const framesDecoded = info.framesDecoded || 0
    this.inboundVideoCurFrameReceived = Math.max(
      framesReceived - this.lastInboundVideoFramesReceived,
      0
    )
    this.inboundVideoCurFramesDecoded = Math.max(
      framesDecoded - this.lastInboundVideoFramesDecoded,
      0
    )
    this.lastInboundVideoFramesReceived = framesReceived
    this.lastInboundVideoFramesDecoded = framesDecoded

    //  视频分辨率 Store frame dimensions and other properties
    this.inboundVideoFrameWidth = info.frameWidth || 0
    this.inboundVideoFrameHeight = info.frameHeight || 0

    const codecMimeType = this.resolveCodecMimeType(info.codecId, codecById)
    if (codecMimeType) {
      this.codecMimeType = codecMimeType
      if (codecMimeType !== this.lastLoggedVideoCodec) {
        this.lastLoggedVideoCodec = codecMimeType
        console.log(
          "SDP 视频统计，inbound video视频编码格式 codec:",
          codecMimeType
        )
      }
    }

    // 解码器名：仅在浏览器显式暴露 decoderImplementation 时记录真实解码器
    this.inboundVideoDecoder =
      typeof info.decoderImplementation === "string"
        ? info.decoderImplementation
        : "unknown"
    if (
      this.inboundVideoDecoder !== "unknown" &&
      this.inboundVideoDecoder !== this.lastLoggedVideoEncoder
    ) {
      this.lastLoggedVideoEncoder = this.inboundVideoDecoder
      console.log("SDP 视频统计，解码器decoder:", this.inboundVideoDecoder)
    }

    // Store raw inbound values
    this.inboundVideoFramesReceived = framesReceived
    this.inboundVideoFramesDecoded = framesDecoded
    this.inboundVideoPacketsReceived = packetsReceived
    this.inboundVideoFramesDropped = info.framesDropped || 0
    this.inboundVideoKeyFramesDecoded = info.keyFramesDecoded || 0
    this.inboundVideoFecPacketsReceived = info.fecPacketsReceived || 0
    this.inboundVideoFecBytesReceived = info.fecBytesReceived || 0

    if (
      this.inboundVideoFecPacketsReceived !==
        this.lastLoggedInboundVideoFecPacketsReceived ||
      this.inboundVideoFecBytesReceived !==
        this.lastLoggedInboundVideoFecBytesReceived
    ) {
      this.lastLoggedInboundVideoFecPacketsReceived =
        this.inboundVideoFecPacketsReceived
      this.lastLoggedInboundVideoFecBytesReceived =
        this.inboundVideoFecBytesReceived
      // console.log(
      //   "SDP 视频统计 inbound-rtp(video) FEC:",
      //   "fecPacketsReceived=",
      //   this.inboundVideoFecPacketsReceived,
      //   "fecBytesReceived=",
      //   this.inboundVideoFecBytesReceived
      // )
    }

    if (this.csvHelper) {
      const values: number[] = headers.map((field) => {
        if (field === "freezeCount") return this.inboundVideoFreezeCount
        if (field === "totalFreezesDuration")
          return this.inboundVideoTotalFreezesDuration
        return info[field]
      })
      this.csvHelper.writeData(values)
      this.csvHelper.writeData([this.inboundVideoDecoder !== "unknown" ? 1 : 0])
    }
  }

  private processInboundRTPAudio(info: any) {
    const bytesReceived = info.bytesReceived || 0
    const diffBytesReceived = Math.max(
      bytesReceived - this.lastInboundAudioBytesReceived,
      0
    )
    this.lastInboundAudioBytesReceived = bytesReceived
    this.inboundAudioIsPlayingAudio = diffBytesReceived > 0
  }

  private processOutboundRTPVideo(info: any, codecById: Map<string, any>) {
    const bytesSent: number = info.bytesSent
    this.outboundVideoBytesPerSecond = Math.max(
      bytesSent - this.lastOutboundBytesSent,
      0
    )
    this.lastOutboundBytesSent = bytesSent
    if (info.framesPerSecond !== undefined) {
      this.outboundVideoFps = Math.round(Number(info.framesPerSecond))
    }
    if (info.frameWidth !== undefined)
      this.outboundVideoFrameWidth = info.frameWidth
    if (info.frameHeight !== undefined)
      this.outboundVideoFrameHeight = info.frameHeight
    if (info.encoderImplementation)
      this.outboundVideoEncoder = info.encoderImplementation
    else {
      const codecMimeType = this.resolveCodecMimeType(info.codecId, codecById)
      if (codecMimeType) this.outboundVideoEncoder = codecMimeType
    }
    if (info.totalEncodeTime !== undefined)
      this.outboundVideoTotalEncodeTime = info.totalEncodeTime
    if (info.framesEncoded !== undefined)
      this.outboundVideoFramesEncoded = info.framesEncoded
    if (info.mid) {
      this.outboundVideoMid = info.mid
    }
  }

  private processOutboundRTPAudio(info: any) {
    const bytesSent: number = info.bytesSent || 0
    this.outboundAudioBytesPerSecond = Math.max(
      bytesSent - this.lastOutboundAudioBytesSent,
      0
    )
    this.lastOutboundAudioBytesSent = bytesSent

    if (info.encoderImplementation) {
      this.outboundAudioEncoder = info.encoderImplementation
    }

    if (info.mid) {
      this.outboundAudioMid = info.mid
    }
  }

  private processRemoteInboundRTPVideo(info: any) {
    const packetsLost: number = info.packetsLost || 0
    const packetsReceived: number = info.packetsReceived || 0
    const diffLost = packetsLost - this.lastRemoteInboundPacketsLost
    const diffRecv = packetsReceived - this.lastRemoteInboundPacketsReceived
    if (diffLost + diffRecv > 0) {
      this.remoteInboundVideoLostRate = diffLost / (diffLost + diffRecv)
    }
    this.lastRemoteInboundPacketsLost = packetsLost
    this.lastRemoteInboundPacketsReceived = packetsReceived

    if (info.jitter !== undefined) this.remoteInboundVideoJitter = info.jitter

    // 部分 Safari 版本只在 remote-inbound-rtp 中提供 RTT。
    if (
      typeof info.roundTripTime === "number" &&
      Number.isFinite(info.roundTripTime) &&
      (info.roundTripTime > 0 || this.candidatePairCurrentRtt <= 0)
    ) {
      this.candidatePairCurrentRtt = info.roundTripTime * 1000
    }
  }

  public getStateInfo(report: RTCStatsReport): ConnStateInfo {
    const codecById = new Map<string, any>()
    let selectedCandidatePairId: string | undefined
    report.forEach((info) => {
      const codecId = info.id as unknown
      if (info.type === "codec" && typeof codecId === "string") {
        codecById.set(codecId, info)
      }
      if (
        info.type === "transport" &&
        typeof info.selectedCandidatePairId === "string"
      ) {
        selectedCandidatePairId = info.selectedCandidatePairId
      }
    })

    // 每轮 getStats 重置 outbound video 的展示字段为初始值。对齐 android
    // 端每轮重新遍历 outbound-rtp 报告的语义：若本轮无 outbound-rtp video
    // 报告（摄像头已关闭/track 已移除），这些字段保持初始值（mid 为空），
    // 使 reportData 的守卫拦截、不再上报 output_video，避免上报陈旧数据。
    // lastOutboundBytesSent 是增量计算状态，不在此重置；bytesSent 增量在
    // processOutboundRTPVideo 中用 max(bytesSent - last, 0) 重新计算。
    this.outboundVideoMid = ""
    this.outboundVideoFrameWidth = 0
    this.outboundVideoFrameHeight = 0
    this.outboundVideoFps = 0
    this.outboundVideoEncoder = null
    this.outboundVideoTotalEncodeTime = 0
    this.outboundVideoFramesEncoded = 0
    this.outboundVideoBytesPerSecond = 0

    report.forEach((info) => {
      switch (info.type) {
        case "candidate-pair":
          if (
            selectedCandidatePairId !== undefined &&
            info.id !== selectedCandidatePairId
          ) {
            break
          }
          this.processCandidatePair(info)
          break
        case "transport":
          this.processTransport(info)
          break
        case "inbound-rtp":
          if (info.kind === "video")
            this.processInboundRTPVideo(info, codecById)
          else if (info.kind === "audio") this.processInboundRTPAudio(info)
          break
        case "outbound-rtp":
          if (info.kind === "video")
            this.processOutboundRTPVideo(info, codecById)
          else if (info.kind === "audio") this.processOutboundRTPAudio(info)
          break
        case "remote-inbound-rtp":
          if (info.kind === "video") this.processRemoteInboundRTPVideo(info)
          break
        default:
          break
      }
    })

    return {
      // 上报数据：输入视频流
      inputVideoData: {
        fps: this.inboundVideoFps, // 帧率 1 （显示：动态帧率）
        curBytesReceived: this.inboundVideoCurBytesReceived, // 码率 （显示：码率）
        lostRate: this.inboundVideoLostRate, // 丢包率 1 （显示：丢包率）
        curFrameRecv: this.inboundVideoCurFrameReceived, // 计算当前 1s 内收到的帧数 1
        curFramesDecoded: this.inboundVideoCurFramesDecoded, // 计算当前 1s 内解码的帧数 1
        codec: this.codecMimeType, // 视频编码协议  如：video/H264这种视频格式 1

        fecPacketsReceived: this.inboundVideoFecPacketsReceived, // 接收的FEC包（flexfec）
        fecBytesReceived: this.inboundVideoFecBytesReceived, // 接收的FECByte（flexfec）
        frameWidth: this.inboundVideoFrameWidth, // 视频分辨率 1 （显示：分辨率）
        frameHeight: this.inboundVideoFrameHeight, // 视频分辨率 1 （显示：分辨率）
        decoder: this.inboundVideoDecoder, // 浏览器使用的解码器名 1
        freezeCount: this.inboundVideoFreezeCount, // 卡顿次数 1
        totalFreezesDuration: this.inboundVideoTotalFreezesDuration, // 卡顿总时长 1
        keyFramesDecoded: this.inboundVideoKeyFramesDecoded, // 累计解码的关键帧数
        framesDropped: this.inboundVideoFramesDropped, // 累计丢弃的帧数
        framesReceived: this.inboundVideoFramesReceived, // 累计收到的帧数
        packetsReceived: this.inboundVideoPacketsReceived, // 累计收到的数据包数
        framesDecoded: this.inboundVideoFramesDecoded, // 累计解码的帧数
      },

      otherData: {
        rtt: this.candidatePairCurrentRtt, // 网络往返延迟 1 （显示：延迟、上报数据：输入视频流）
        transportCurBytesReceived: this.transportCurBytesReceived, // 码率 transport中的，是总的  （上报数据：输入视频流）
        isPlayingAudio: this.inboundAudioIsPlayingAudio, // 是否正在播放音频 1
      },
      // 上报数据：输出视频流
      outputVideoData: {
        mid: this.outboundVideoMid, // 输出的视频流id（outbound-rtp 的 mid）
        frameWidth: this.outboundVideoFrameWidth, // 视频宽度
        frameHeight: this.outboundVideoFrameHeight, // 视频高度
        fps: this.outboundVideoFps, // 帧率
        bytesSent: this.outboundVideoBytesPerSecond, // 需要计算  // 发送的字节数
        encoder: this.outboundVideoEncoder, // 编码码器名
        totalEncodeTime: this.outboundVideoTotalEncodeTime, //  编码总时长
        framesEncoded: this.outboundVideoFramesEncoded, // 累计编码的帧数
        lostRate: this.remoteInboundVideoLostRate, // 需要计算  丢包率
        jitter: this.remoteInboundVideoJitter, // 抖动
      },
    }
  }

  async prepareWriteToCSVFile() {
    if (this.csvHelper) return
    const _csvHelper = new CSVHelper("web")
    const isSuccess = await _csvHelper.init()
    if (isSuccess) {
      await _csvHelper.writeHeader(headers)
      this.csvHelper = _csvHelper
    }
  }

  async finish() {
    if (this.inFreeze) {
      const nowSec = Date.now() / 1000
      this.inboundVideoTotalFreezesDuration += nowSec - this.freezeStartTime
      this.inFreeze = false
    }
    this.csvHelper?.finish()
    this.csvHelper = undefined
  }
}
